import React, { useState, useCallback, memo, useRef, useEffect } from "react";
import "./OptimizedImage.scss";
import NotFoundImg from "@/assets/common/not-found.png";

class LRUCache<K, V> {
  private cache: Map<K, V>;
  private maxSize: number;

  constructor(maxSize: number) {
    this.cache = new Map();
    this.maxSize = maxSize;
  }

  get(key: K): V | undefined {
    if (!this.cache.has(key)) {
      return undefined;
    }
    const value = this.cache.get(key)!;
    this.cache.delete(key);
    this.cache.set(key, value);
    return value;
  }

  set(key: K, value: V): void {
    if (this.cache.has(key)) {
      this.cache.delete(key);
    }
    this.cache.set(key, value);
    if (this.cache.size > this.maxSize) {
      const firstKey = this.cache.keys().next().value;
      if (firstKey !== undefined) {
        this.cache.delete(firstKey);
      }
    }
  }

  has(key: K): boolean {
    return this.cache.has(key);
  }
}

const imageLoadedCache = new LRUCache<string, boolean>(500);

function isCdnImage(src: string): boolean {
  return src.includes("/static/cdnimg/");
}

// ---------------------------------------------------------------------------
// Cloudflare Image Resizing 自动探测
// 按 origin 粒度记住是否支持，避免重复 404
// ---------------------------------------------------------------------------
const CF_PROBE_KEY = "__cf_resize_support";

/** origin -> true(支持) / false(不支持) */
const cfResizeSupport = new Map<string, boolean>();

function loadCfProbeCache() {
  try {
    const raw = sessionStorage.getItem(CF_PROBE_KEY);
    if (raw) {
      const obj = JSON.parse(raw) as Record<string, boolean>;
      for (const [k, v] of Object.entries(obj)) cfResizeSupport.set(k, v);
    }
  } catch { /* ignore */ }
}
loadCfProbeCache();

function saveCfProbeCache() {
  try {
    const obj: Record<string, boolean> = {};
    cfResizeSupport.forEach((v, k) => { obj[k] = v; });
    sessionStorage.setItem(CF_PROBE_KEY, JSON.stringify(obj));
  } catch { /* ignore */ }
}

function getOriginFromSrc(src: string): string {
  if (src.startsWith("http")) {
    try { return new URL(src).origin; } catch { /* fall through */ }
  }
  return location.origin;
}

const pendingProbes = new Map<string, Promise<boolean>>();

/**
 * 探测一个 origin 是否支持 /cdn-cgi/image/。
 * 用一个极小的探测请求（1x1, quality=1）来测试，只发一次。
 */
function probeCfResize(origin: string, samplePath: string): Promise<boolean> {
  const existing = pendingProbes.get(origin);
  if (existing) return existing;

  const probeUrl = `${origin === location.origin ? "" : origin}/cdn-cgi/image/width=1,height=1,quality=1,format=auto/${samplePath.replace(/^\//, "")}`;

  const promise = new Promise<boolean>(resolve => {
    const img = new Image();
    img.onload = () => {
      cfResizeSupport.set(origin, true);
      saveCfProbeCache();
      resolve(true);
    };
    img.onerror = () => {
      cfResizeSupport.set(origin, false);
      saveCfProbeCache();
      resolve(false);
    };
    img.src = probeUrl;
  });

  pendingProbes.set(origin, promise);
  return promise;
}

/**
 * 获取当前 origin 对 CF Image Resizing 的支持状态。
 * - true:  支持
 * - false: 不支持
 * - undefined: 还未探测完
 */
function getCfResizeStatus(origin: string): boolean | undefined {
  return cfResizeSupport.get(origin);
}

// ---------------------------------------------------------------------------

function buildCfImageUrl(
  src: string,
  containerWidth: number,
  containerHeight: number,
  quality: number
): string {
  if (!isCdnImage(src)) return src;

  const origin = getOriginFromSrc(src);
  const status = getCfResizeStatus(origin);
  if (status === false) return src;

  const dpr = Math.min(window.devicePixelRatio || 1, 3);
  const w = Math.ceil(containerWidth * dpr);
  const h = Math.ceil(containerHeight * dpr);

  const params: string[] = [];
  if (w > 0) params.push(`width=${w}`);
  if (h > 0) params.push(`height=${h}`);
  params.push(`quality=${Math.round(quality * 100)}`);
  params.push("format=auto");
  params.push("fit=cover");

  const cfPrefix = `/cdn-cgi/image/${params.join(",")}`;

  if (src.startsWith("http")) {
    try {
      const url = new URL(src);
      return `${url.origin}${cfPrefix}${url.pathname}`;
    } catch {
      return src;
    }
  }

  return `${cfPrefix}${src}`;
}

/** 从 /cdn-cgi/image/... 的 URL 中还原出原始图片路径 */
function stripCfPrefix(cfUrl: string): string {
  const marker = "/cdn-cgi/image/";
  const idx = cfUrl.indexOf(marker);
  if (idx === -1) return cfUrl;

  const afterMarker = cfUrl.substring(idx + marker.length);
  const slashIdx = afterMarker.indexOf("/");
  if (slashIdx === -1) return cfUrl;

  const originalPath = afterMarker.substring(slashIdx);

  if (idx > 0 && cfUrl.startsWith("http")) {
    try {
      const origin = new URL(cfUrl).origin;
      return `${origin}${originalPath}`;
    } catch { /* fall through */ }
  }

  return originalPath;
}

export interface OptimizedImageProps {
  src: string;
  alt: string;
  className?: string;
  width?: number | string;
  height?: number | string;
  placeholder?: string;
  loading?: "lazy" | "eager";
  onLoad?: () => void;
  onError?: () => void;
  onImageLoad?: (e: React.SyntheticEvent<HTMLImageElement>) => void;
  fallback?: string;
  quality?: "low" | "medium" | "high";
  priority?: boolean;
  onClick?: () => void;
  objectFit?: "cover" | "contain" | "fill" | "none";
  maxRetries?: number;
  retryDelay?: number;
}

export const OptimizedImage: React.FC<OptimizedImageProps> = memo(
  ({
    src,
    alt,
    className = "",
    width,
    height,
    onClick,
    placeholder,
    loading = "lazy",
    onLoad,
    onError,
    onImageLoad,
    fallback = NotFoundImg,
    quality = "medium",
    priority = false,
    objectFit = "cover",
    maxRetries = 3,
    retryDelay = 1000,
  }) => {
    const qualityMap = {
      low: 0.5,
      medium: 0.75,
      high: 1,
    };

    const getOptimizedSrc = useCallback(
      (originalSrc: string) => {
        if (
          !originalSrc ||
          originalSrc.startsWith("data:") ||
          originalSrc.startsWith("blob:")
        ) {
          return originalSrc;
        }

        if (originalSrc.startsWith("http")) {
          return originalSrc;
        }

        if (originalSrc.startsWith("/static/")) {
          return originalSrc;
        }

        const qualityValue = qualityMap[quality];
        const separator = originalSrc.includes("?") ? "&" : "?";
        return `${originalSrc}${separator}q=${qualityValue}`;
      },
      [quality]
    );

    const optimizedSrc = getOptimizedSrc(src);

    const [imageLoaded, setImageLoaded] = useState(
      imageLoadedCache.get(optimizedSrc) || false
    );
    const [imageError, setImageError] = useState(false);
    const [retryCount, setRetryCount] = useState(0);
    const [currentSrc, setCurrentSrc] = useState(optimizedSrc);
    const [isInView, setIsInView] = useState(priority || loading === "eager");
    const [containerSize, setContainerSize] = useState<{
      w: number;
      h: number;
    } | null>(null);
    // CF Resizing 探测完成后触发重新计算 finalSrc
    const [cfProbeReady, setCfProbeReady] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const observerRef = useRef<IntersectionObserver | null>(null);
    const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    // 记录当前图片是否正在使用 CF URL（用于 fallback 判断）
    const usingCfUrlRef = useRef(false);

    const handleLoad = useCallback(
      (e: React.SyntheticEvent<HTMLImageElement>) => {
        setImageLoaded(true);
        imageLoadedCache.set(optimizedSrc, true);

        // CF URL 成功加载 → 标记该 origin 支持
        if (usingCfUrlRef.current) {
          const origin = getOriginFromSrc(optimizedSrc);
          if (getCfResizeStatus(origin) === undefined) {
            cfResizeSupport.set(origin, true);
            saveCfProbeCache();
          }
        }

        onLoad?.();
        onImageLoad?.(e);
      },
      [onLoad, onImageLoad, optimizedSrc]
    );

    const handleError = useCallback(() => {
      // 如果当前用的是 CF URL，先尝试回退到原始图片
      if (usingCfUrlRef.current) {
        const origin = getOriginFromSrc(optimizedSrc);
        cfResizeSupport.set(origin, false);
        saveCfProbeCache();
        usingCfUrlRef.current = false;
        // 回退到原始图片（去掉 /cdn-cgi/image/ 前缀）
        setCurrentSrc(optimizedSrc);
        setRetryCount(0);
        return;
      }

      if (retryCount < maxRetries && !imageError) {
        retryTimeoutRef.current = setTimeout(
          () => {
            setRetryCount(prev => prev + 1);
            const separator = optimizedSrc.includes("?") ? "&" : "?";
            setCurrentSrc(`${optimizedSrc}${separator}_retry=${Date.now()}`);
            setImageError(false);
          },
          retryDelay * (retryCount + 1)
        );
      } else {
        setImageError(true);
        onError?.();
      }
    }, [retryCount, maxRetries, retryDelay, optimizedSrc, imageError, onError]);

    useEffect(() => {
      const isCached = imageLoadedCache.get(optimizedSrc);
      if (isCached) {
        setImageLoaded(true);
      } else {
        setImageLoaded(false);
      }
      setImageError(false);
      setRetryCount(0);
      setCurrentSrc(optimizedSrc);
      usingCfUrlRef.current = false;
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    }, [optimizedSrc]);

    // 探测 CF Resizing 支持（只对 CDN 图片）
    useEffect(() => {
      if (!isCdnImage(optimizedSrc)) {
        setCfProbeReady(true);
        return;
      }
      const origin = getOriginFromSrc(optimizedSrc);
      const status = getCfResizeStatus(origin);
      if (status !== undefined) {
        setCfProbeReady(true);
        return;
      }
      // 提取 /static/cdnimg/... 路径作为探测样本
      const match = optimizedSrc.match(/(\/static\/cdnimg\/[^\s?#]+)/);
      const samplePath = match ? match[1] : "/static/cdnimg/v2/test";
      probeCfResize(origin, samplePath).then(() => setCfProbeReady(true));
    }, [optimizedSrc]);

    useEffect(() => {
      if (loading === "eager" || priority) {
        setIsInView(true);
        return;
      }

      let retryTimer: NodeJS.Timeout | null = null;
      let mounted = true;

      const setupObserver = () => {
        if (!mounted) return;

        if (!containerRef.current) {
          retryTimer = setTimeout(setupObserver, 50);
          return;
        }

        observerRef.current = new IntersectionObserver(
          entries => {
            entries.forEach(entry => {
              if (entry.isIntersecting && mounted) {
                setIsInView(true);
                observerRef.current?.disconnect();
              }
            });
          },
          {
            root: null,
            rootMargin: "200px",
            threshold: 0.01,
          }
        );

        observerRef.current.observe(containerRef.current);
      };

      setupObserver();

      return () => {
        mounted = false;
        if (retryTimer) {
          clearTimeout(retryTimer);
        }
        observerRef.current?.disconnect();
      };
    }, [priority, loading, src]);

    useEffect(() => {
      if (!containerRef.current) return;
      const el = containerRef.current;
      const rect = el.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        setContainerSize({ w: rect.width, h: rect.height });
      }
    }, [isInView]);

    useEffect(() => {
      return () => {
        if (observerRef.current) {
          observerRef.current.disconnect();
        }
        if (retryTimeoutRef.current) {
          clearTimeout(retryTimeoutRef.current);
        }
      };
    }, []);

    useEffect(() => {
      const handleReload = () => {
        if (imageError || retryCount > 0) {
          setImageError(false);
          setRetryCount(0);
          setImageLoaded(false);
          const separator = optimizedSrc.includes("?") ? "&" : "?";
          setCurrentSrc(`${optimizedSrc}${separator}_reload=${Date.now()}`);
        }
      };

      window.addEventListener("image-reload-trigger", handleReload);
      return () => {
        window.removeEventListener("image-reload-trigger", handleReload);
      };
    }, [imageError, retryCount, optimizedSrc]);

    const shouldLoad = isInView || priority || loading === "eager";

    const finalSrc = (() => {
      if (imageError) return fallback;
      const s = currentSrc;
      if (containerSize && isCdnImage(s) && cfProbeReady) {
        const cfUrl = buildCfImageUrl(
          s,
          containerSize.w,
          containerSize.h,
          qualityMap[quality]
        );
        // 如果 buildCfImageUrl 返回了 CF URL（含 /cdn-cgi/），标记当前在使用 CF
        usingCfUrlRef.current = cfUrl.includes("/cdn-cgi/image/");
        return cfUrl;
      }
      usingCfUrlRef.current = false;
      return s;
    })();

    return (
      <div
        ref={containerRef}
        className={`optimized-image ${className}`}
        style={{ width, height }}
      >
        {!shouldLoad && placeholder && (
          <div
            className="optimized-image__placeholder"
            style={{
              backgroundImage: `url(${placeholder})`,
              width: "100%",
              height: "100%",
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
        )}

        {shouldLoad && (
          <img
            src={finalSrc}
            alt={alt}
            className={`optimized-image__img ${imageLoaded ? "loaded" : ""}`}
            onLoad={handleLoad}
            onError={handleError}
            loading={loading}
            decoding="async"
            onClick={onClick}
            style={{
              width: "100%",
              height: "100%",
              objectFit,
              opacity: imageLoaded ? 1 : 0,
              transition: "opacity 0.3s ease-in-out",
            }}
          />
        )}

        {!imageLoaded && shouldLoad && !placeholder && (
          <div className="optimized-image__skeleton">
            <div className="skeleton-content" />
          </div>
        )}
      </div>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.src === nextProps.src &&
      prevProps.quality === nextProps.quality &&
      prevProps.priority === nextProps.priority &&
      prevProps.loading === nextProps.loading
    );
  }
);

OptimizedImage.displayName = "OptimizedImage";

export default OptimizedImage;
